import type { MeetingPayloadData } from '../../types';

import { AxiosError } from 'axios';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { App } from 'antd';

import { axiosClient } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';

type Payload = {
  companyID: number;
  body: MeetingPayloadData;
};

type ResponseData = { message: string };

export const addMeeting = async ({ companyID, body }: Payload): Promise<ResponseData> => {
  const { data } = await axiosClient.post<ResponseData>(`/companies/${companyID}/meetings`, body);
  return data;
};
type UseAddMeetingOptions = {
  config?: Partial<UseMutationOptions<ResponseData, AxiosError<{ error?: string; message?: string }>, Payload>>;
  postSuccess?: () => void;
  // Return true when the form has displayed the error inline.
  postError?: (message: string, body: MeetingPayloadData) => boolean;
};

export const useAddMeeting = ({ config, postSuccess, postError }: UseAddMeetingOptions) => {
  const { notification: toaster } = App.useApp();
  return useMutation({
    onError: (error, { body }) => {
      const message = error.response?.data?.error || error.response?.data?.message || error.message;
      if (postError?.(message, body)) return;
      toaster.error({
        message: 'Something Went Wrong',
        description: message,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['meetings'],
      });
      toaster.success({
        message: 'Added Successfully',
        description: 'Meeting Added Successfully',
      });
      postSuccess?.();
    },
    ...config,
    mutationFn: addMeeting,
  });
};
